import React, { useState } from "react";
import { ArrowLeftRight, Sparkles, Wallet, Check } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { Modal } from "./Modal";

const CAT_LABEL = { decoration: "plan.decoration", ceia: "plan.ceia", table: "budget.table", gifts: "nav.gifts", others: "budget.others" };
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// Raising a category budget must NEVER silently change it. There are only two
// conscious ways: (1) redistribute from another category (total unchanged), or
// (2) review/raise the TOTAL budget and then apply. Otherwise the original stays.
export function useBudgetAllocate() {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const fmt = (v) => formatMoney(symbol, v || 0);
  const [st, setSt] = useState(null); // {category, old, next, delta, free, remaining, budget, onDone, stage, budgetAfter}
  const [take, setTake] = useState({});
  const [newTotal, setNewTotal] = useState("");

  const apply = async (payload, onDone, freedMsg) => {
    const { data } = await api.post("/budget/allocate", payload);
    toast.success(freedMsg || t("alloc.done"));
    onDone?.(data.budget);
  };

  const allocate = async (category, newLimit, onDone) => {
    const next = Number(newLimit) || 0;
    const { data } = await api.get("/budget");
    const b = data.budget;
    const old = b.categories[category]?.limit || 0;
    const delta = round2(next - old);
    if (b.total.limit == null) { toast.error(t("alloc.set_total_first")); return; }
    if (delta === 0) { onDone?.(b); return; }
    if (delta < 0) { await apply({ category, new_limit: next }, onDone, t("alloc.freed", { v: fmt(-delta) })); return; }
    const free = Math.max(0, b.total.free_balance ?? 0);
    const remaining = round2(delta - free);
    if (remaining <= 0) { await apply({ category, new_limit: next }, onDone); return; } // free absorbs it
    setTake({});
    setSt({ category, old, next, delta, free, remaining, budget: b, onDone, stage: "ask" });
  };

  const setStage = (stage) => setSt((s) => ({ ...s, stage }));
  const cancel = () => { const cb = st?.onDone; setSt(null); cb?.(null, true); };

  // -- redistribution helpers --
  const donors = st ? Object.entries(st.budget.categories)
    .filter(([k, v]) => k !== st.category && (v.available || 0) > 0)
    .map(([k, v]) => ({ key: k, available: round2(v.available || 0) })) : [];
  const selected = round2(Object.values(take).reduce((a, v) => a + (Number(v) || 0), 0));
  const missing = st ? round2(st.remaining - selected) : 0;
  const suggest = () => {
    if (!st) return;
    let need = st.remaining; const out = {};
    for (const d of donors) {
      if (need <= 0) break;
      const amt = Math.min(d.available, need);
      if (amt > 0) { out[d.key] = amt; need = round2(need - amt); }
    }
    setTake(out);
  };
  const confirmRedis = async () => {
    const s = st; setSt(null);
    await apply({ category: s.category, new_limit: s.next, mode: "redistribute",
      redistribute: Object.fromEntries(Object.entries(take).map(([k, v]) => [k, Number(v) || 0])) }, s.onDone)
      .catch((e) => toast.error(e.response?.data?.detail || "Error"));
  };

  // -- review total helpers --
  const openReviewTotal = () => {
    const suggested = round2(st.budget.total.limit + st.remaining);
    setNewTotal(String(suggested));
    setStage("review_total");
  };
  const confirmTotal = async () => {
    const nt = round2(newTotal);
    const minT = round2(st.budget.total.limit + st.remaining);
    if (nt < minT) { toast.error(t("review.min_hint", { v: fmt(minT) })); return; }
    try {
      await api.put("/budget", { total: nt });
      const { data } = await api.get("/budget");
      toast.success(t("review.total_saved"));
      setSt((s) => ({ ...s, stage: "apply_q", budgetAfter: data.budget }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const applyYes = async () => {
    const s = st; setSt(null);
    await apply({ category: s.category, new_limit: s.next }, s.onDone)
      .catch((e) => toast.error(e.response?.data?.detail || "Error"));
  };
  const applyNo = () => { const s = st; setSt(null); s.onDone?.(s.budgetAfter); };

  let node = null;
  if (st) {
    const catL = t(CAT_LABEL[st.category] || "budget.others");
    const suggested = round2(st.budget.total.limit + st.remaining);
    node = (
      <Modal open onClose={cancel} title={t("alloc.title")} testid="alloc-modal" centerTitle>
        {/* DECISION — three explicit actions (no ambiguous yes/no) */}
        {st.stage === "ask" && (
          <div className="space-y-5 text-sm">
            <p className="text-xmas-snow" data-testid="alloc-ask-body">
              {t("review.ask_body", { cat: catL, old: fmt(st.old), new: fmt(st.next), delta: fmt(st.remaining) })}
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={() => setStage("redistribute")} data-testid="alloc-ask-redistribute" className="btn-gold inline-flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold">
                <ArrowLeftRight className="h-4 w-4" /> {t("review.act_redistribute", { v: fmt(st.remaining) })}
              </button>
              <button onClick={openReviewTotal} data-testid="alloc-ask-increase" className="inline-flex items-center justify-center gap-2 rounded-xl border border-xmas-gold/40 py-3 text-sm font-semibold text-xmas-snow hover:bg-white/5">
                <Wallet className="h-4 w-4" /> {t("review.act_increase")}
              </button>
              <button onClick={cancel} data-testid="alloc-ask-cancel" className="rounded-xl py-2.5 text-sm text-xmas-slate hover:text-xmas-snow">
                {t("review.act_cancel")}
              </button>
            </div>
          </div>
        )}

        {/* DECISION 1 = YES — redistribute */}
        {st.stage === "redistribute" && (
          <div className="space-y-4 text-sm">
            <div className="rounded-xl border border-xmas-gold/20 bg-white/5 p-4" data-testid="alloc-redistribute">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-xmas-champagne">{t("alloc.from_title", { v: fmt(st.remaining) })}</p>
                <button onClick={suggest} data-testid="alloc-suggest" className="inline-flex items-center gap-1 text-xs text-xmas-gold hover:underline"><Sparkles className="h-3 w-3" /> {t("alloc.suggest")}</button>
              </div>
              <div className="space-y-2">
                {donors.length === 0 && <p className="text-xs text-xmas-slate">—</p>}
                {donors.map((d) => (
                  <div key={d.key} className="flex items-center justify-between gap-2" data-testid={`alloc-donor-${d.key}`}>
                    <div className="min-w-0"><p className="truncate text-sm text-xmas-snow">{t(CAT_LABEL[d.key])}</p><p className="text-[10px] text-xmas-slate">{t("alloc.available")}: {fmt(d.available)}</p></div>
                    <div className="flex items-center gap-1"><span className="text-xs text-xmas-slate">{symbol}</span>
                      <input type="number" min="0" max={d.available} value={take[d.key] ?? ""} data-testid={`alloc-take-${d.key}`}
                        onChange={(e) => setTake((p) => ({ ...p, [d.key]: e.target.value }))}
                        className="w-20 rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-2 py-1 text-right text-sm text-xmas-snow outline-none focus:border-xmas-gold" placeholder="0" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-white/10 pt-2 text-xs">
                <span className="text-xmas-slate">{t("alloc.need_lbl")}: <b className="text-xmas-snow">{fmt(st.remaining)}</b></span>
                <span className="text-xmas-slate">{t("alloc.selected")}: <b className="text-xmas-snow" data-testid="alloc-selected">{fmt(selected)}</b></span>
                {missing > 0 && <span className="text-xmas-crimson">{t("alloc.missing")}: <b data-testid="alloc-missing">{fmt(missing)}</b></span>}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={confirmRedis} disabled={missing !== 0} data-testid="alloc-confirm-redis" className="btn-gold inline-flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold disabled:opacity-40"><ArrowLeftRight className="h-4 w-4" /> {t("alloc.confirm_redis")}</button>
              <button onClick={() => setStage("ask")} data-testid="alloc-redis-back" className="rounded-xl py-2.5 text-sm text-xmas-slate hover:text-xmas-snow">{t("alloc.back")}</button>
            </div>
          </div>
        )}

        {/* DECISION 1 = NO — offer to review total */}
        {st.stage === "review_q" && (
          <div className="space-y-5 text-sm">
            <div className="rounded-xl border border-xmas-gold/20 bg-white/5 p-4" data-testid="alloc-review-q">
              <p className="mb-1 font-display text-base text-xmas-gold">{t("review.q2_title")}</p>
              <p className="text-xmas-slate">{t("review.q2_body", { cat: catL, old: fmt(st.old), delta: fmt(st.remaining) })}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={openReviewTotal} data-testid="alloc-review-yes" className="btn-gold inline-flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold"><Wallet className="h-4 w-4" /> {t("review.review")}</button>
              <button onClick={cancel} data-testid="alloc-review-no" className="rounded-xl border border-xmas-gold/30 py-3 text-sm font-semibold text-xmas-snow hover:bg-white/5">{t("review.keep")}</button>
            </div>
          </div>
        )}

        {/* DECISION 2 = YES — edit total budget */}
        {st.stage === "review_total" && (
          <div className="space-y-4 text-sm">
            <p className="text-xmas-slate" data-testid="alloc-review-total-body">
              {t("review.total_body", { cat: catL, old: fmt(st.old), new: fmt(st.next), inc: fmt(st.remaining) })}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-[10px] uppercase tracking-wider text-xmas-slate">{t("review.current")}</p>
                <p className="font-mono-num text-lg text-xmas-snow" data-testid="alloc-current-total">{fmt(st.budget.total.limit)}</p>
              </div>
              <div className="rounded-xl border border-xmas-gold/25 bg-xmas-gold/5 p-3">
                <p className="text-[10px] uppercase tracking-wider text-xmas-gold">{t("review.suggested")}</p>
                <p className="font-mono-num text-lg text-xmas-gold" data-testid="alloc-suggested-total">{fmt(suggested)}</p>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-xmas-slate">{t("review.new_total_label")}</label>
              <div className="flex items-center gap-2">
                <span className="text-xl text-xmas-gold">{symbol}</span>
                <input type="number" min={suggested} value={newTotal} data-testid="alloc-new-total-input"
                  onChange={(e) => setNewTotal(e.target.value)}
                  className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 font-mono-num text-xl text-xmas-snow outline-none focus:border-xmas-gold" />
              </div>
              <p className="mt-1 text-[11px] text-xmas-slate">{t("review.min_hint", { v: fmt(suggested) })}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button onClick={cancel} data-testid="alloc-total-cancel" className="glass flex-1 rounded-xl py-3 text-sm font-semibold text-xmas-snow">{t("review.cancel")}</button>
              <button onClick={confirmTotal} data-testid="alloc-total-confirm" className="btn-gold flex-1 rounded-xl py-3 text-sm font-bold">{t("review.confirm_total")}</button>
            </div>
          </div>
        )}

        {/* AFTER TOTAL RAISED — apply to category? (preserves original intent) */}
        {st.stage === "apply_q" && (
          <div className="space-y-5 text-sm">
            <p className="text-xmas-snow" data-testid="alloc-apply-body">{t("review.apply_body", { cat: catL, new: fmt(st.next) })}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button onClick={applyNo} data-testid="alloc-apply-no" className="glass flex-1 rounded-xl py-3 text-sm font-semibold text-xmas-snow">{t("review.apply_no")}</button>
              <button onClick={applyYes} data-testid="alloc-apply-yes" className="btn-gold inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold"><Check className="h-4 w-4" /> {t("review.apply_yes")}</button>
            </div>
          </div>
        )}
      </Modal>
    );
  }
  return { allocate, node };
}
